// The Moon's terrain material: real albedo, the small crater field, and a
// regolith reflectance law.
//
// Albedo is streamed at up to 83 m per pixel (moonimagery.js): the NASA CGI
// Moon Kit colour (Hapke-normalised LROC WAC, no shading) with the WAC mosaic's
// detail, over the global albedo map (public/textures/moon-tiles/albedo.png).
// It is calibrated to normal albedo (maria ~0.07, highlands ~0.16) keeping the
// Kit's colour ratios, which are the Moon's own faint browns and blues.
//
// Craters smaller than the data resolves (craters.js) are drawn twice from one
// definition: as mesh relief where a terrain ring is fine enough, and here as
// shading where it is not. The shader walks the same 3D lattice with the same
// integer hash as the CPU, so a crater lit by its normal at 3 km is the crater
// the mesh draws when the lander gets close, and fresh craters' bright ejecta
// sit around exactly those bowls.
//
// Reflectance: a Hapke model in its practical form. Lommel-Seeliger geometry
// (grains shadowing grains: no Lambert falloff at grazing sun), a two-lobe
// Henyey-Greenstein phase function weighted to backscatter, which is why the
// Moon reads bright looking down-sun and dark looking into the sun, the
// shadow-hiding opposition surge (the halo round the camera's own shadow),
// and Chandrasekhar's H functions for the little light that scatters twice.
// Normalised at 60 degrees phase, the geometry of most photographs, so the
// calibrated albedo is what typical ground returns; the surge then reads up
// to 2.7x that at the anti-solar point and up-sun ground 0.65x. No sky fill:
// the Moon's shadows are black but for the ground's own bounce and
// earthshine, which is a millionth of sunlight.
import * as THREE from 'three';
import { CRATER_CLASSES, cumulativeN } from './craters.js';
import { moonImageryUniforms, MOON_IMAGERY_GLSL } from './moonimagery.js';
import { MOON_ALBEDO_URL, ALBEDO_SCALE } from './moondem.js';
import { KIT_ALBEDO } from '../data/moonImagerySource.js';
import { HORIZON_GLSL } from './moonshadow.js';
import { getTexture } from './textures.js';


let albedoTex = null;
function albedoTexture() {
  if (!albedoTex) {
    albedoTex = new THREE.TextureLoader().load(MOON_ALBEDO_URL);
    albedoTex.colorSpace = THREE.NoColorSpace;
    albedoTex.wrapS = THREE.RepeatWrapping;
    albedoTex.wrapT = THREE.ClampToEdgeWrapping;
    albedoTex.anisotropy = 8;
  }
  return albedoTex;
}

// Share of a class's full (4.2 Gyr) count held by a surface T Gyr old, at
// T = 3.2, 3.4 ... 4.2, per class, for the shader to interpolate.
const T_SAMPLES = [3.2, 3.4, 3.6, 3.8, 4.0, 4.2];
function ageTable() {
  const out = [];
  for (const K of CRATER_CLASSES) {
    const lo = K.d / Math.SQRT2 / 1000, hi = K.d * Math.SQRT2 / 1000;
    const full = cumulativeN(lo, 4.2) - cumulativeN(hi, 4.2);
    for (const T of T_SAMPLES) out.push(K.d > 60 ? (cumulativeN(lo, T) - cumulativeN(hi, T)) / full : 1);
  }
  return out;
}

const NC = CRATER_CLASSES.length;

// Mirror of craters.js: hash, visit, profile, freshness.
const CRATER_GLSL = /* glsl */`
const int NC = ${NC};
uniform vec4 uClass[NC];          // d, cell, p, seed
uniform float uAgeTab[${NC * T_SAMPLES.length}];
uniform float uMoonR;
float crHash(ivec3 c, int k) {
  uint h = (uint(c.x) * 374761393u) ^ (uint(c.y) * 668265263u) ^ (uint(c.z) * 2147483647u) ^ (uint(k) * 1274126177u);
  h = (h ^ (h >> 13u)) * 1103515245u;
  h ^= h >> 16u;
  return float(h) / 4294967296.0;
}
float crProfile(float r, float R, float depth, float rim, float fresh, float flo) {
  float q = r / R;
  float k = 2.0 + 2.5 * fresh;
  if (q < 1.0) {
    float h = -depth + (depth + rim) * pow(q, k);
    if (flo > 0.0) h = max(h, -depth * (1.0 - flo));
    float crest = 0.25 - 0.18 * fresh;
    if (q > 1.0 - crest) {
      float t = (q - (1.0 - crest)) / crest;
      float s = t * t * (1.0 - fresh) * 0.6;
      h = h * (1.0 - s) + rim * s;
    }
    return h;
  }
  float w = 0.18 + 0.5 * (1.0 - fresh);
  float fall = exp(-((q - 1.0) / w) * ((q - 1.0) / w));
  float apron = fresh * rim * 0.35 / (q * q * q);
  return rim * fall + (q < 3.0 ? apron * (1.0 - (q - 1.0) / 2.0) : 0.0);
}
float crAgeFactor(int c, float T) {
  float t = clamp((T - 3.2) / 0.2, 0.0, 4.999);
  int i = int(floor(t));
  float f = t - float(i);
  return mix(uAgeTab[c * 6 + i], uAgeTab[c * 6 + i + 1], f);
}
// Walk one class at surface-fixed offset v (m) from the patch centre. The
// lattice is addressed relative to a per-class integer base the CPU computes
// in double precision (setMoonPatchFrame), so cell positions keep centimetre
// precision in float32 anywhere on the Moon. Accumulates the height gradient
// (when wN > 0), fresh-ejecta cover (when wF > 0) and the shadow a crater's
// rim casts on its own floor (sunS: unit sun direction, surface-fixed).
uniform ivec3 uClassBase[NC];
uniform vec3 uClassFrac[NC];
uniform float uClassB2[NC];
uniform vec3 uPatchC;
uniform float uPatchRc;
void crClass(int c, vec3 v, float T, float wN, float wF, vec3 sunS, inout vec3 grad, inout float fresh, inout float shadow, inout float bowl) {
  vec4 K = uClass[c];
  float f = uMoonR / K.y;
  float cv = dot(uPatchC, v), vv = dot(v, v);
  float dP = (2.0 * uPatchRc * cv + vv) / (sqrt(uPatchRc * uPatchRc + 2.0 * uPatchRc * cv + vv) + uPatchRc);
  float Pl = uPatchRc + dP;
  vec3 dir = normalize(uPatchC * uPatchRc + v);
  vec3 pr = uClassFrac[c] - uPatchC * (f * dP / Pl) + v * (f / Pl);
  ivec3 ipr = ivec3(floor(pr));
  vec3 B = vec3(uClassBase[c]);
  int seed = int(K.w);
  float sunUp = dot(sunS, dir);
  vec3 sunH = sunS - dir * sunUp;
  float sunHL = length(sunH);
  vec3 sh = sunH / max(sunHL, 1e-6);
  float tanE = sunUp / max(sunHL, 1e-6);
  for (int a = -1; a <= 1; a++) for (int b = -1; b <= 1; b++) for (int e = -1; e <= 1; e++) {
    ivec3 rel = ipr + ivec3(a, b, e);
    ivec3 cc = uClassBase[c] + rel;
    if (crHash(cc, seed) > K.z) continue;
    vec3 o = vec3(rel) + vec3(crHash(cc, seed + 1), crHash(cc, seed + 2), crHash(cc, seed + 3));
    float num = uClassB2[c] + 2.0 * dot(B, o) + dot(o, o);
    float delta = num / (sqrt(max(0.0, f * f + num)) + f);
    if (abs(delta) > 0.5) continue;
    float len = f + delta;
    vec3 d = (pr - o * (f / len) + B * (delta / len)) * K.y;
    float reach = K.x * 1.41421356 * 1.5;
    if (any(greaterThan(abs(d), vec3(reach)))) continue;
    if (K.x > 60.0 && crHash(cc, seed + 4) > crAgeFactor(c, T)) continue;
    float D = K.x * pow(2.0, crHash(cc, seed + 5) - 0.5);
    float R = D * 0.5;
    float fr = pow(crHash(cc, seed + 6), 5.0);
    float r0 = length(d);
    if (wF > 0.0 && K.x >= 40.0 && fr >= 0.5) {
      float qq = r0 / R;
      if (qq <= 3.0) fresh = max(fresh, wF * (fr - 0.5) / 0.5 * (qq < 1.0 ? 0.8 : max(0.0, 1.0 - (qq - 1.0) / 2.0)));
    }
    float ea = crHash(cc, seed + 7) * 3.14159265, el = 0.12 * crHash(cc, seed + 8);
    vec3 axis = vec3(cos(ea), sin(ea) * 0.7071, sin(ea) * 0.7071);
    float along = dot(d, axis);
    float r = sqrt(max(0.0, dot(d, d) + along * along * (1.0 / ((1.0 + el) * (1.0 + el)) - 1.0)));
    if (r > R * 3.0 || r0 < 1e-3) continue;
    float depth = D * (0.025 + 0.175 * pow(fr, 0.7));
    float rim = D * (0.004 + 0.036 * fr);
    float regolith = 4.0 + 6.0 * crHash(cc, seed + 9);
    float flo = (D < regolith * 10.0 && fr > 0.2) ? 0.35 : 0.0;
    float h0 = crProfile(r, R, depth, rim, fr, flo);
    // How enclosed the point is by this bowl's walls (for their bounce light).
    if (r < R) bowl = max(bowl, (1.0 - (r / R) * (r / R)) * clamp(depth / D * 6.0, 0.0, 1.0));
    // Rim shadow on the floor: from this point toward the sun, where the ray
    // leaves the bowl it must clear the rim.
    if (r < R && tanE > 0.0) {
      vec3 dt = d - dir * dot(d, dir);
      float ds = dot(dt, sh);
      float t = -ds + sqrt(max(0.0, ds * ds - dot(dt, dt) + R * R));
      float clear = h0 + t * tanE - rim;
      float pen = max(0.004 * t, 0.05);
      shadow = min(shadow, smoothstep(-pen, pen, clear));
    }
    if (wN <= 0.0) continue;
    float eps = max(0.02 * R, 0.05);
    float dh = (crProfile(r + eps, R, depth, rim, fr, flo) - crProfile(max(0.0, r - eps), R, depth, rim, fr, flo)) / (2.0 * eps);
    grad += wN * dh * (d / r0);
  }
}
`;

// Regolith reflectance (see the header).
const LAMBERT_DIRECT = 'reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );';
const MOON_DIRECT = /* glsl */`
	{
		float rgMu0 = max( dotNL, 0.0 );
		float rgMu = saturate( dot( geometryNormal, geometryViewDir ) );
		float rgCosG = clamp( dot( directLight.direction, geometryViewDir ), -1.0, 1.0 );
		float rgG = acos( rgCosG );
		// Hapke parameters for mature regolith (Sato et al. 2014, LROC WAC):
		// HG lobe width b, backscatter share c, surge amplitude B0 and width h,
		// gamma = sqrt(1 - w) for single-scattering albedo w = 0.35.
		const float rgB = 0.25, rgC = 0.8, rgB0 = 1.0, rgH = 0.06, rgGamma = 0.806;
		const float rgB2 = rgB * rgB;
		float rgP = rgC * ( 1.0 - rgB2 ) / pow( 1.0 + rgB2 - 2.0 * rgB * rgCosG, 1.5 ) + ( 1.0 - rgC ) * ( 1.0 - rgB2 ) / pow( 1.0 + rgB2 + 2.0 * rgB * rgCosG, 1.5 );
		float rgBg = rgB0 / ( 1.0 + tan( 0.5 * rgG ) / rgH );
		float rgH0 = ( 1.0 + 2.0 * rgMu0 ) / ( 1.0 + 2.0 * rgGamma * rgMu0 );
		float rgHv = ( 1.0 + 2.0 * rgMu ) / ( 1.0 + 2.0 * rgGamma * rgMu );
		float rgF = ( 1.0 + rgBg ) * rgP + rgH0 * rgHv - 1.0;
		// The same at the reference geometry: 60 degrees phase, both angles 45.
		const float rgPr = rgC * ( 1.0 - rgB2 ) / pow( 1.0 + rgB2 - rgB, 1.5 ) + ( 1.0 - rgC ) * ( 1.0 - rgB2 ) / pow( 1.0 + rgB2 + rgB, 1.5 );
		const float rgBr = rgB0 / ( 1.0 + 0.57735 / rgH );
		const float rgHr = ( 1.0 + 1.41421 ) / ( 1.0 + 1.41421 * rgGamma );
		const float rgFr = ( 1.0 + rgBr ) * rgPr + rgHr * rgHr - 1.0;
		// The Apollo Hasselblads were exposed for the ground: sunlit regolith
		// prints as a light grey, a stop over what a scene exposed for Earth's
		// brighter landscapes gives it. rgGain is that stop, in part.
		const float rgGain = 1.3;
		float rgLS = min( 2.0 / max( rgMu0 + rgMu, 1e-3 ), 8.0 ) * rgGain;
		// Terrain shadow (LOLA horizon march, crater rims) on the sun only.
		float rgSun = step( 0.999, dot( directLight.direction, uSunView ) );
		reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor ) * rgLS * ( rgF / rgFr ) * mix( 1.0, gMoonShadow, rgSun );
		// One bounce off sunlit regolith into shadow (see gMoonBounce), for
		// ground the sun does not reach: shadowed, or turned away from it.
		float rgLit = gMoonShadow * smoothstep( 0.0, 0.12, dotNL );
		reflectedLight.directDiffuse += directLight.color * BRDF_Lambert( material.diffuseColor ) * rgSun * ( 1.0 - rgLit ) * gMoonBounce;
	}
`;

// Ring layout of a terrain patch (terrain.js specs), for which crater classes
// the mesh already draws: half-widths and cell sizes, innermost first.
export const MOON_RINGS = { half: [410.15625, 1640.625, 3750, 56250], cell: [4.8828125, 19.53125, 78.125, 1171.875, 14062.5] };

// Shared by every Moon terrain material, set each frame by moonsurface.js:
// the sun direction in view space and in the Moon's surface-fixed frame, and
// the crater classes the quality tier affords.
// uScour: blast zones under landed craft (rovertracks.js sets them each
// frame): patch-local x, z, radius (m) and strength, up to four.
export const MOON_SCOURS = 4;
export const moonSunUniforms = {
  uSunView: { value: new THREE.Vector3(0, 1, 0) }, uSunS: { value: new THREE.Vector3(0, 1, 0) }, uCraterMax: { value: NC },
  uScour: { value: Array.from({ length: MOON_SCOURS }, () => new THREE.Vector4()) }, uScourN: { value: 0 }
};

// Per patch: the frame the shader's crater lattice is addressed in (see
// crClass). basis: { up, east, north } unit vectors, Rc: centre radius (m).
export function setMoonPatchFrame(m, basis, Rc) {
  const u = m.userData.uniforms;
  if (!u || !u.uClassBase) return;
  u.uPatchC.value.copy(basis.up);
  u.uPatchE.value.copy(basis.east);
  u.uPatchN.value.copy(basis.north);
  u.uPatchRc.value = Rc;
  u.uPatchH.value = Rc - 1737400;
  CRATER_CLASSES.forEach((K, i) => {
    const f = 1737400 / K.cell;
    const cx = basis.up.x * f, cy = basis.up.y * f, cz = basis.up.z * f;
    const bx = Math.floor(cx), by = Math.floor(cy), bz = Math.floor(cz);
    u.uClassBase.value[i].set(bx, by, bz);
    u.uClassFrac.value[i].set(cx - bx, cy - by, cz - bz);
    u.uClassB2.value[i] = bx * bx + by * by + bz * bz - f * f;
  });
}

export function makeMoonMaterial({ transparent = false, detail = true, lat0Deg = 0, lon0Deg = 0 } = {}) {
  const m = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, metalness: 0, transparent, depthWrite: true, side: THREE.FrontSide });
  m.userData.uniforms = {
    ...moonImageryUniforms,
    uAlbedoMap: { value: albedoTexture() },
    uAlbedoMapScale: { value: ALBEDO_SCALE },
    uKitAlbedo: { value: new THREE.Vector2(KIT_ALBEDO.a, KIT_ALBEDO.b) },
    uImageryOrigin: { value: new THREE.Vector2(lon0Deg, lat0Deg) },
    uTexOrigin: { value: new THREE.Vector2() },
    uDetail: { value: detail ? 1 : 0 },
    uMoonR: { value: 1737400 },
    uClass: { value: CRATER_CLASSES.map(K => new THREE.Vector4(K.d, K.cell, K.p, K.seed)) },
    uAgeTab: { value: ageTable() },
    uRingHalf: { value: new THREE.Vector4(...MOON_RINGS.half) },
    uRingCell: { value: MOON_RINGS.cell.slice() },
    ...moonSunUniforms,
    uClassBase: { value: CRATER_CLASSES.map(() => new THREE.Vector3()) },
    uClassFrac: { value: CRATER_CLASSES.map(() => new THREE.Vector3()) },
    uClassB2: { value: CRATER_CLASSES.map(() => 0) },
    uPatchC: { value: new THREE.Vector3(0, 1, 0) },
    uPatchE: { value: new THREE.Vector3(1, 0, 0) },
    uPatchN: { value: new THREE.Vector3(0, 0, -1) },
    uPatchRc: { value: 1737400 },
    uPatchH: { value: 0 },
    uHzNear: { value: null },
    uHzMid: { value: null },
    uHzFar: { value: null },
    uHzReady: { value: 0 },
    uFill: { value: 0.0 },
    // The photographic regolith tile (public/textures/rocketry/moon_regolith,
    // 2048 px over uRegolithScale metres) and its normal map, for the ground
    // within a hundred metres or so: the clods, crumbs and pits the
    // procedural octaves only hint at. uRegolithRelief scales the slope the
    // normal map adds, uRegolithMottle how much of the tile's own brightness
    // variation reaches the albedo (the calibrated mean is kept either way).
    uRegolithA: { value: detail ? getTexture('moon_regolith', { anisotropy: 16 }) : null },
    uRegolithN: { value: detail ? getTexture('moon_regolith_n', { anisotropy: 16, colorSpace: THREE.NoColorSpace }) : null },
    uRegolithScale: { value: 1.5 },
    uRegolithRelief: { value: 1.0 },
    uRegolithMottle: { value: 0.45 }
  };
  m.userData.moonFrame = true;
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, m.userData.uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
attribute float rim;
attribute vec2 geo;
varying float vRim;
varying vec2 vGeo;
varying vec3 vLocalPos;
varying vec3 vEastV;
varying vec3 vSouthV;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
vRim = rim; vGeo = geo; vLocalPos = position;
vEastV = normalize(normalMatrix * vec3(1.0, 0.0, 0.0));
vSouthV = normalize(normalMatrix * vec3(0.0, 0.0, 1.0));`);
    const lights = THREE.ShaderChunk.lights_physical_pars_fragment;
    if (lights.includes(LAMBERT_DIRECT)) {
      shader.fragmentShader = shader.fragmentShader.replace('#include <lights_physical_pars_fragment>', lights.replace(LAMBERT_DIRECT, MOON_DIRECT));
    }
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
varying float vRim;
varying vec2 vGeo;
varying vec3 vLocalPos;
varying vec3 vEastV;
varying vec3 vSouthV;
uniform vec3 uSunView;
uniform vec3 uSunS;
uniform vec3 uPatchE;
uniform vec3 uPatchN;
uniform sampler2D uAlbedoMap;
uniform float uAlbedoMapScale;
uniform vec2 uKitAlbedo;
uniform vec2 uImageryOrigin;
uniform vec2 uTexOrigin;
uniform float uDetail;
uniform vec4 uRingHalf;
uniform float uRingCell[5];
uniform int uCraterMax;
uniform float uFill;
uniform vec4 uScour[${MOON_SCOURS}];
uniform int uScourN;
uniform sampler2D uRegolithA;
uniform sampler2D uRegolithN;
uniform float uRegolithScale;
uniform float uRegolithRelief;
uniform float uRegolithMottle;
${MOON_IMAGERY_GLSL}
${CRATER_GLSL}
${HORIZON_GLSL}
float moonAge(float a) {
  if (a > 0.2) return 4.2 - 3.0 * min(1.0, (a - 0.2) / 0.08);
  return 3.4 + 0.8 * smoothstep(0.0, 1.0, clamp((a - 0.08) / 0.05, 0.0, 1.0));
}
float moonMeshCell(vec2 xz) {
  float m = max(abs(xz.x), abs(xz.y));
  if (m < uRingHalf.x) return uRingCell[0];
  if (m < uRingHalf.y) return uRingCell[1];
  if (m < uRingHalf.z) return uRingCell[2];
  if (m < uRingHalf.w) return uRingCell[3];
  return uRingCell[4];
}
vec3 gMoonCraterGrad = vec3(0.0);
vec3 gMoonMicro = vec3(0.0, 0.0, 1.0);
vec2 gMoonTexSlope = vec2(0.0);
float gMoonShadow = 1.0;
float gMoonFresh = 0.0;
vec3 gMoonDir = vec3(0.0, 1.0, 0.0);
float gMoonAlbedo = 0.12;
// Irradiance reaching shadowed ground off the sunlit regolith around it, as a
// fraction of full sun: the surroundings' albedo times the lit share of the
// sky they fill, weighted by how squarely the sun strikes them (walls facing
// a low sun take it nearly head on). About 2 % on open ground and 8 % on a
// crater floor ringed by lit walls. Lit plain under a low sun, with the
// regolith's Lommel-Seeliger lift, reads about ten times brighter than such
// a floor; under a high sun about fifteen. Dark, never punched-out black.
float gMoonBounce = 0.0;
// Periodic gradient noise with derivatives (value in xy, d/dp in zw); the
// period P (cells) keeps it seamless across the patch texture frame's fold.
vec2 mnGrad(vec2 i, float P) {
  // Integer hash: a sin() hash of cell indices in the tens of thousands has
  // no precision left and lines the gradients up into streaks.
  ivec2 c = ivec2(mod(i, P));
  uint h = (uint(c.x) * 374761393u) ^ (uint(c.y) * 668265263u);
  h = (h ^ (h >> 13u)) * 1103515245u;
  h ^= h >> 16u;
  float a = float(h) / 4294967296.0 * 6.2831853;
  return vec2(cos(a), sin(a));
}
vec3 mnNoised(vec2 p, float P) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  vec2 du = 30.0 * f * f * (f * (f - 2.0) + 1.0);
  vec2 ga = mnGrad(i, P), gb = mnGrad(i + vec2(1.0, 0.0), P), gc = mnGrad(i + vec2(0.0, 1.0), P), gd = mnGrad(i + vec2(1.0, 1.0), P);
  float va = dot(ga, f), vb = dot(gb, f - vec2(1.0, 0.0)), vc = dot(gc, f - vec2(0.0, 1.0)), vd = dot(gd, f - vec2(1.0, 1.0));
  float v = va + u.x * (vb - va) + u.y * (vc - va) + u.x * u.y * (va - vb - vc + vd);
  vec2 d = ga + u.x * (gb - ga) + u.y * (gc - ga) + u.x * u.y * (ga - gb - gc + gd)
         + du * (u.yx * (va - vb - vc + vd) + vec2(vb, vc) - va);
  return vec3(v, d);
}
// Regolith micro-relief under the crater field: slope (east, south) and an
// albedo factor. Seven octaves from 3.2 m to 5 cm, each faded out below two
// pixels, octaves offset so no lattice lines up. The Apollo panoramas show
// ground that undulates by a few degrees at the metre scale and only gets
// rough at the scale of clods and grit, so the slope grows toward the fine
// end; a steep metre-scale field turned a low sun into a black-and-white
// mottle nothing like the photographs. The albedo mottle is faint: mature
// regolith is the same grey everywhere at these scales.
// How worked the ground is here, 0.35 (undisturbed cake: the powder settled
// smooth, the Apollo "fine flour" look) to 1.15 (crumbly, clod-strewn, the
// ground round small fresh craters and where ejecta came down), on a 9 m and
// a 2.3 m lattice. One crumb texture at one strength everywhere read as
// textured plaster; the photographs show the two kinds of ground side by
// side within a few metres.
float moonRough(vec2 tp) {
  float a = mnNoised(tp / 9.0 + vec2(2.5, 7.1), 768.0).x;
  float b = mnNoised(tp / 2.3 + vec2(5.3, 1.9), 3005.0).x;
  return mix(0.22, 1.0, smoothstep(-0.22, 0.34, a * 0.75 + b * 0.45));
}
// Dimples: the craters below the classes crClass walks (2.75 m and up), from
// 10 cm to 1.6 m, which the Apollo ground is covered in: soft-rimmed bowls a
// twelfth as deep as they are wide, most of them degraded to a saucer. Two
// hashed lattices; the slope is analytic, so nothing is sampled and nothing
// sparkles, and each fades out below two pixels.
// sunL: the sun in the patch's (east, up, south) axes. shadow: the floor of
// a bowl the sun does not reach over its rim, the black crescents every
// Apollo frame has in its small craters.
vec2 moonDimples(vec2 tp, float ps, vec3 sunL, inout float shadow) {
  vec2 slope = vec2(0.0);
  vec2 sh2 = vec2(sunL.x, sunL.z);
  float sunHL = length(sh2);
  sh2 /= max(sunHL, 1e-6);
  float tanE = sunL.y / max(sunHL, 1e-6);
  const float cell[2] = float[2](3.0, 0.85);
  const float dmin[2] = float[2](0.45, 0.10);
  const float dmax[2] = float[2](1.6, 0.45);
  const float prob[2] = float[2](0.5, 0.42);
  for (int k = 0; k < 2; k++) {
    float vis = smoothstep(1.5 * ps, 4.0 * ps, dmin[k]);
    if (vis <= 0.0) continue;
    vec2 gp = (tp + vec2(11.7, 3.3) * float(k)) / cell[k];
    vec2 gi = floor(gp);
    float period = 6912.0 / cell[k];
    for (int a = -1; a <= 1; a++) for (int b = -1; b <= 1; b++) {
      vec2 ci = gi + vec2(float(a), float(b));
      ivec2 c = ivec2(mod(ci, period));
      uint h = (uint(c.x) * 374761393u) ^ (uint(c.y) * 668265263u) ^ (uint(k + 1) * 1274126177u);
      h = (h ^ (h >> 13u)) * 1103515245u;
      h ^= h >> 16u;
      float r0 = float(h) / 4294967296.0;
      if (r0 > prob[k]) continue;
      vec2 centre = ci + vec2(float((h >> 8u) & 255u), float((h >> 16u) & 255u)) / 255.0;
      float D = mix(dmin[k], dmax[k], pow(float((h >> 24u) & 255u) / 255.0, 2.2));
      float R = D * 0.5;
      vec2 d = (gp - centre) * cell[k];
      float r = length(d);
      if (r > R * 1.6 || r < 1e-4) continue;
      float fresh = float(h & 255u) / 255.0;
      float depth = D * (0.07 + 0.09 * fresh);
      float rim = D * (0.006 + 0.016 * fresh);
      // Bowl: a raised cosine; rim: a soft ring just outside it.
      float w = 0.3 * R;
      float x = (r - R) / w;
      float dh;
      if (r < R) {
        dh = depth * (1.5707963 / R) * sin(3.14159265 * r / R);
        if (tanE > 0.0 && k == 0) {
          // Height in the bowl (the bowl plus the rim's inner flank, so a
          // point on the rim is at the rim's own height and casts nothing on
          // itself), and where the ray toward the sun leaves the bowl.
          float h0 = -depth * 0.5 * (1.0 + cos(3.14159265 * r / R)) + rim * exp(-x * x);
          float ds = dot(d, sh2);
          float t = -ds + sqrt(max(0.0, ds * ds - dot(d, d) + R * R));
          float clear = h0 + t * tanE - rim;
          float pen = max(0.08 * R, 2.0 * ps);
          float visD = vis * smoothstep(1.5 * ps, 4.0 * ps, D);
          shadow = min(shadow, mix(1.0, smoothstep(-pen, pen, clear), visD));
        }
      } else dh = 0.0;
      dh += -2.0 * x / w * rim * exp(-x * x) * smoothstep(1.0, 0.6, r / R);
      slope += (d / r) * dh * vis * smoothstep(1.5 * ps, 4.0 * ps, D);
    }
  }
  return slope;
}
vec3 moonMicro(vec2 tp, float ps, float rough) {
  vec2 slope = vec2(0.0);
  float mottle = 0.0;
  float s = 3.2;
  // Smooth at the metre scale (a corrugation there reads as wet sand under
  // any sun), all the roughness in the last three octaves: clods and grit.
  const float amp[7] = float[7](0.012, 0.009, 0.007, 0.009, 0.016, 0.034, 0.05);
  for (int k = 0; k < 7; k++) {
    // An octave needs four pixels a cell to read as relief: at two it is a
    // shimmer of slopes the mips have not yet averaged away, and under a
    // low sun the Lommel-Seeliger law turns that into a glitter of ripples.
    float w = smoothstep(3.0 * ps, 8.0 * ps, s);
    if (w <= 0.0) break;
    // Each octave on its own lattice orientation: shared axes weave.
    float ra = 0.61 * float(k);
    float ca = cos(ra), sa = sin(ra);
    vec2 rp = vec2(ca * tp.x - sa * tp.y, sa * tp.x + ca * tp.y);
    vec3 n = mnNoised(rp / s + vec2(17.3, 5.1) * float(k), 6912.0 / s);
    // The slope back in the ground's axes.
    n.yz = vec2(ca * n.y + sa * n.z, -sa * n.y + ca * n.z);
    // The clod octaves follow how worked the ground is.
    slope += n.yz * amp[k] * w * (k >= 4 ? rough : 1.0);
    if (k < 2) mottle += n.x * 0.008 * w;
    s *= 0.5;
  }
  return vec3(slope, 1.0 + mottle);
}
// The photographic regolith tile: albedo factor (mean 1, so the calibrated
// albedo survives) and the slope (east, south) its normal map carries. Two
// layers of the one tile. Coarse: the tile over four times uRegolithScale,
// so its centimetre clods become the 5-10 cm lumps that give the Apollo
// ground its shadowed, crumbly look at a few metres; two rotated samples
// picked by a slow noise so its 6 m repeat never lines up. Fine: the tile at
// its own scale for the grain within a few tens of metres. Each layer fades
// out where a pixel covers more than its features: past that the mips have
// averaged it flat and the procedural octaves carry the look.
float moonRegolithTex(vec2 tp, float ps, float rough, out vec2 slope) {
  slope = vec2(0.0);
  // The coarse layer's clods are 5-10 cm, the fine layer's 1-2 cm: each is
  // held until a clod spans about four pixels, and gone at two. Held longer
  // they read as a crumb pattern stamped over everything at 20-50 m, which
  // is a distance the Apollo frames show as smooth ground.
  float wc = 1.0 - smoothstep(0.014, 0.036, ps);
  if (wc <= 0.0) return 1.0;
  float wf = 1.0 - smoothstep(0.0035, 0.011, ps);
  const float ca = -0.6663, sa = 0.7457; // cos, sin of 2.3 rad
  const float cb = 0.3624, sb = -0.9320; // cos, sin of -1.2 rad
  const vec3 LUMW = vec3(0.2126, 0.7152, 0.0722);
  float mean = max(dot(textureLod(uRegolithA, vec2(0.5), 12.0).rgb, LUMW), 1e-3);
  // Coarse layer.
  float sc4 = uRegolithScale * 4.0;
  vec2 uv1 = vec2(ca * tp.x - sa * tp.y, sa * tp.x + ca * tp.y) / sc4 + 0.37;
  vec2 uv2 = vec2(cb * tp.x - sb * tp.y, sb * tp.x + cb * tp.y) / (sc4 * 1.19) + 0.71;
  // Pick on a 27 m lattice: 6912 / 27 = 256 cells across the fold.
  float pick = smoothstep(-0.12, 0.12, mnNoised(tp / 27.0 + vec2(3.7, 8.2), 256.0).x);
  vec3 n1 = texture2D(uRegolithN, uv1).xyz * 2.0 - 1.0;
  vec3 n2 = texture2D(uRegolithN, uv2).xyz * 2.0 - 1.0;
  // OpenGL-convention map sampled with u east and v south: the slope is
  // -n.xy / n.z, turned back from the sample's rotation into the ground's axes.
  // The map's slopes average 2 (63 degrees): a clod texture baked with the
  // height exaggerated. Scaled to a 9 degree mean, the cake of the Apollo
  // frames, before the relief and the worked-ground factor go on.
  const float TILE_NORM = 0.08;
  vec2 s1 = -n1.xy / max(n1.z, 0.2) * TILE_NORM;
  vec2 s2 = -n2.xy / max(n2.z, 0.2) * TILE_NORM;
  s1 = vec2(ca * s1.x + sa * s1.y, -sa * s1.x + ca * s1.y);
  s2 = vec2(cb * s2.x + sb * s2.y, -sb * s2.x + cb * s2.y);
  float l1 = dot(texture2D(uRegolithA, uv1).rgb, LUMW);
  float l2 = dot(texture2D(uRegolithA, uv2).rgb, LUMW);
  float fc = clamp(mix(l1, l2, pick) / mean, 0.3, 2.5);
  slope = mix(s1, s2, pick) * uRegolithRelief * wc * rough;
  float f = mix(1.0, fc, uRegolithMottle * wc * rough);
  // Fine layer.
  if (wf > 0.0) {
    vec2 uvf = tp / uRegolithScale;
    vec3 nf = texture2D(uRegolithN, uvf).xyz * 2.0 - 1.0;
    slope += (-nf.xy / max(nf.z, 0.2)) * (TILE_NORM * 0.7 * uRegolithRelief * wf * mix(0.7, 1.0, rough));
    float ff = clamp(dot(texture2D(uRegolithA, uvf).rgb, LUMW) / mean, 0.3, 2.5);
    f *= mix(1.0, ff, 0.6 * uRegolithMottle * wf * mix(0.7, 1.0, rough));
  }
  return f;
}
// Grit: rock chips and glass a centimetre or two across, a few percent of the
// ground, some bright, most dark with their own shadowed pits. Seen within a
// few metres; beyond that it is the fine speckle of the photographs.
float moonGrit(vec2 tp, float ps, out float bright) {
  bright = 0.0;
  float vis = 1.0 - smoothstep(0.012, 0.035, ps);
  if (vis <= 0.0) return 0.0;
  vec2 gp = (tp + vec2(3.7, 9.1)) / 0.04;
  vec2 gi = floor(gp);
  ivec2 c = ivec2(mod(gi, 172800.0));
  uint h = (uint(c.x) * 374761393u) ^ (uint(c.y) * 668265263u);
  h = (h ^ (h >> 13u)) * 1103515245u;
  h ^= h >> 16u;
  float r = float(h) / 4294967296.0;
  if (r > 0.045) return 0.0;
  vec2 gc = fract(gp) - 0.5 + 0.3 * (vec2(float(h & 255u), float((h >> 8u) & 255u)) / 255.0 - 0.5);
  float d = length(gc);
  float size = 0.07 + 0.16 * pow(float((h >> 16u) & 255u) / 255.0, 2.0);
  bright = r < 0.008 ? 1.0 : 0.0;
  return (1.0 - smoothstep(size * 0.6, size, d)) * vis;
}
`)
      // Albedo and craters first (normals need the crater gradient).
      .replace('#include <map_fragment>', `#include <map_fragment>
{
  vec2 ll = vGeo + uImageryOrigin;
  vec2 mapUV = ll / vec2(360.0, 180.0) + 0.5;
  float latR = radians(ll.y), lonR = radians(ll.x);
  gMoonDir = vec3(cos(latR) * cos(lonR), sin(latR), -cos(latR) * sin(lonR));
  const vec3 LUMW = vec3(0.2126, 0.7152, 0.0722);
  float mapAlbedo = texture2D(uAlbedoMap, vec2(fract(mapUV.x), mapUV.y)).r * uAlbedoMapScale;
  // The fallback in the Kit's own linear units, so one calibration serves both.
  vec3 kitFallback = vec3(pow(mapAlbedo / uKitAlbedo.x, 1.0 / uKitAlbedo.y));
  vec3 kit = moonImagerySample(mapUV, kitFallback).rgb;
  float kitLum = max(dot(kit, LUMW), 1e-4);
  float albedo = uKitAlbedo.x * pow(kitLum, uKitAlbedo.y);
  // The Kit's own faint browns and blues, at half strength: the Apollo
  // frames read as a near-neutral grey with a warm cast, not a brown.
  // The film recorded the regolith as a neutral grey under the same sun the
  // scene's light carries a warm tint for, so that tint is taken back out.
  vec3 hue = mix(vec3(1.0), kit / kitLum, 0.18) * vec3(0.945, 0.98, 1.04);
  float T = moonAge(mapAlbedo);
  // Crater classes the mesh does not draw here, and fresh ejecta for all.
  float ps = max(length(dFdx(vLocalPos)), length(dFdy(vLocalPos)));
  float meshCell = moonMeshCell(vLocalPos.xz);
  // Patch-local (east, up, south) to a surface-fixed offset from the centre.
  vec3 vS = uPatchE * vLocalPos.x + uPatchC * vLocalPos.y - uPatchN * vLocalPos.z;
  float crShadow = 1.0, crBowl = 0.0;
  for (int c = 0; c < NC; c++) {
    if (c >= uCraterMax) break;
    float D = uClass[c].x;
    float vis = smoothstep(1.2 * ps, 3.0 * ps, D);
    if (vis <= 0.0) continue;
    // The mesh carries classes of six cells and 30 m up (terrain.js).
    float wN = D >= max(6.0 * meshCell, 30.0) ? 0.0 : vis;
    float wF = D >= 40.0 ? vis : 0.0;
    crClass(c, vS, T, wN, wF, uSunS, gMoonCraterGrad, gMoonFresh, crShadow, crBowl);
  }
  vec3 sunL = vec3(dot(uSunS, uPatchE), dot(uSunS, uPatchC), -dot(uSunS, uPatchN));
  float hzVis = hzSun(vLocalPos, sunL);
  gMoonShadow = hzVis * crShadow;
  // Ground in the terrain's shadow under a sun well up is in a depression
  // (a crater bigger than the procedural field) with lit walls around it.
  float enclosed = max(crBowl, (1.0 - hzVis) * smoothstep(0.05, 0.3, sunL.y));
  gMoonBounce = 0.8 * max(albedo, 0.06) * (0.25 + 0.75 * enclosed);
  // Fresh ejecta: bright blocky debris, up to twice the local albedo.
  albedo = mix(albedo, max(albedo * 1.6, 0.17), gMoonFresh * 0.5);
  // Blast zones: a descent engine blows the fine top layer off the ground
  // under it, leaving a smoother, brighter disc scored with radial streaks
  // (the orbital cameras still see one round every Apollo descent stage),
  // and a faint darker ring where that dust came down.
  for (int i = 0; i < ${MOON_SCOURS}; i++) {
    if (i >= uScourN) break;
    vec2 dv = vLocalPos.xz - uScour[i].xy;
    float d = length(dv) / max(uScour[i].z, 0.1);
    if (d >= 1.0) continue;
    float ang = atan(dv.y, dv.x);
    float streak = 0.5 + 0.5 * sin(ang * 29.0 + 2.2 * sin(ang * 7.0 + d * 9.0) + d * 4.0);
    float centre = 1.0 - smoothstep(0.1, 0.9, d);
    albedo *= 1.0 + uScour[i].w * (0.22 * centre * (0.65 + 0.35 * streak) - 0.05 * smoothstep(0.75, 0.92, d) * (1.0 - smoothstep(0.92, 1.0, d)));
  }
  if (uDetail > 0.5) {
    vec2 tp = vLocalPos.xz + uTexOrigin;
    float rough = moonRough(tp);
    gMoonMicro = moonMicro(tp, ps, rough);
    albedo *= gMoonMicro.z;
    albedo *= moonRegolithTex(tp, ps, rough, gMoonTexSlope);
    float dimpleShadow = 1.0;
    gMoonTexSlope += moonDimples(tp, ps, sunL, dimpleShadow);
    gMoonShadow *= dimpleShadow;
    float gritBright;
    float grit = moonGrit(vLocalPos.xz + uTexOrigin, ps, gritBright);
    if (grit > 0.0) albedo = mix(albedo, albedo * (gritBright > 0.5 ? 2.0 : 0.5), grit);
  }
  gMoonAlbedo = albedo;
  diffuseColor.rgb = albedo * hue;
  diffuseColor.a *= vRim;
}`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
{
  // Crater gradient (body frame) into the patch's east / south axes. Within a
  // patch those are the site's axes to a few tenths of a degree.
  float gE = dot(gMoonCraterGrad, uPatchE), gS = -dot(gMoonCraterGrad, uPatchN);
  normal = normalize(normal - gE * vEastV - gS * vSouthV);
  if (uDetail > 0.5) normal = normalize(normal - (gMoonMicro.xy + gMoonTexSlope).x * vEastV - (gMoonMicro.xy + gMoonTexSlope).y * vSouthV);
}`)
      .replace('#include <lights_fragment_end>', `#include <lights_fragment_end>
reflectedLight.indirectDiffuse *= uFill;
reflectedLight.indirectSpecular *= uFill;`);
  };
  m.customProgramCacheKey = () => 'rocketry-moon-v18-' + (transparent ? 't' : 'o') + (detail ? 'd' : 'p');
  return m;
}
